/**
 * Assessment Value Banner (DC-1367)
 *
 * Layer 1 value display showing the business value of the Compass
 * assessment itself (time savings + risk avoidance).
 * Extracted from StrategicIntelligenceDashboard.tsx.
 */

import React, { useState } from 'react';
import type { AssessmentValue } from '../../types/bridgeIntelligence';

interface AssessmentValueBannerProps {
  assessmentValue?: AssessmentValue;
}

export const AssessmentValueBanner: React.FC<AssessmentValueBannerProps> = ({ assessmentValue }) => {
  const [showExplanation, setShowExplanation] = useState(false);

  if (!assessmentValue) {
    return null;
  }

  const formatCurrency = (val: number): string => {
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
    return `$${val.toLocaleString()}`;
  };

  return (
    <div className="assessment-value-banner">
      <div className="avb-header">
        <span className="avb-label">Assessment Value</span>
        <span className="avb-badge">Layer 1</span>
      </div>

      <div className="avb-metrics">
        <div className="avb-metric">
          <span className="avb-metric-value avb-metric-value--highlight">
            {formatCurrency(assessmentValue.total_value_usd)}
          </span>
          <span className="avb-metric-label">Total Value</span>
        </div>
        <div className="avb-metric">
          <span className="avb-metric-value">
            {assessmentValue.compass_hours < 1
              ? `${Math.round(assessmentValue.compass_hours * 60)}m`
              : assessmentValue.compass_hours < 24
                ? `${Math.round(assessmentValue.compass_hours)}h`
                : `${(assessmentValue.compass_hours / 24).toFixed(1)}d`}
          </span>
          <span className="avb-metric-label">
            Replaces {assessmentValue.manual_weeks} weeks of manual profiling
          </span>
        </div>
        <div className="avb-metric">
          <span className="avb-metric-value">
            {assessmentValue.dimensions_assessed}/{assessmentValue.dimensions_total}
          </span>
          <span className="avb-metric-label">Dimensions assessed</span>
        </div>
        <div className="avb-metric">
          <span className="avb-metric-value">
            {assessmentValue.failure_risk_reduction_pct}%
          </span>
          <span className="avb-metric-label">Risk reduced</span>
        </div>
      </div>

      <button
        className="avb-explanation-toggle"
        onClick={(e) => { e.stopPropagation(); setShowExplanation(!showExplanation); }}
      >
        <span className={`avb-explanation-arrow ${showExplanation ? 'avb-explanation-arrow--open' : ''}`}>
          &#9654;
        </span>
        How is this calculated?
      </button>

      {showExplanation && (
        <div className="avb-explanation">
          <p>
            Manual data profiling across {assessmentValue.dimensions_total} dimensions typically requires
            {' '}{assessmentValue.manual_weeks} weeks of effort for a team
            of {assessmentValue.team_size} ({assessmentValue.manual_effort_hours.toLocaleString()} labor hours
            at ${assessmentValue.hourly_rate.toFixed(0)}/hr = {formatCurrency(assessmentValue.time_savings_usd)}).
            Compass automated this assessment
            in {assessmentValue.compass_hours < 1
              ? `${Math.round(assessmentValue.compass_hours * 60)} minutes`
              : `${Math.round(assessmentValue.compass_hours)} hours`},
            replacing virtually all of that manual effort.
          </p>
          <p>
            By assessing {assessmentValue.dimensions_assessed} of {assessmentValue.dimensions_total} dimensions up front,
            project failure risk dropped from 35% to {35 - assessmentValue.failure_risk_reduction_pct}%,
            avoiding {formatCurrency(assessmentValue.risk_avoidance_usd)} in potential losses
            ({formatCurrency(assessmentValue.project_investment_usd)} project investment x {assessmentValue.failure_risk_reduction_pct}% risk reduction x 50%).
          </p>
        </div>
      )}
    </div>
  );
};
